import { connect } from 'react-redux';
import { withStyles } from '@mui/styles';
import { useFormApi, useFormState } from 'informed';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';

import {
  getPrimaryAddressFromFormStateAnswers,
  primaryAddressRegex,
} from './utility';

// classes and types
import type { $TSFixMe, GenericObject } from '@calefy-inc/utilityTypes';
import { BasicAddress } from '../../../../Typescript';
import type {
  QuoteWizardQuestionInstance,
  QuoteWizardAnswerInstance,
} from '../../../QuoteWizard/classes';
/* import { UnifiedAnswerInstance } from '../../../../Typescript/classes'; */
import { StoreState } from '../../../../store';
import Bugsnag from '@bugsnag/js';
import { errorify } from '../../../../util';
import { Theme } from '@mui/system';
import { useOnLifeOrganization } from '../../../../hooks';

/**
 * A button to fill the location question with the address components (street address, city, province, postal code) of the primary address question. This should only be displayed on policystream and only for the mailing address; purpose is to fill in the mailing address with the person's primary address
 * @param primaryAddress - The mailing address found in the Redux state. Shape: {
 *   address: string | undefined,
 *   city: string | undefined,
 *   province: string | undefined,
 *   postal: string | undefined
 * }
 * @param fillFields - the names of the fields (in the informed FormState) to be filled in. Shape: {
 *   address: string | undefined,
 *   city: string | undefined,
 *   province: string | undefined,
 *   postalCode: string | undefined
 * }
 * @param questionInstanceApiName - The name of the current location question being rendered; used to check that it is not itself the mailing address question
 * @param classes - The theming classes
 */
export interface SameAsPrimaryAddressButtonProps {
  primaryAddress?: BasicAddress;
  fillFields: {
    address?: string;
    city?: string;
    province?: string;
    postalCode?: string;
  };
  questionInstanceApiName?: QuoteWizardQuestionInstance['apiName'];
  classes?: GenericObject;
}
export const UnconnectedSameAsPrimaryAddressButton = ({
  primaryAddress,
  fillFields,
  questionInstanceApiName,
  classes = {},
}: SameAsPrimaryAddressButtonProps) => {
  const onLifeOrg = useOnLifeOrganization();
  const formApi = useFormApi();
  const formState = useFormState();

  let primaryAddressInForm;
  try {
    primaryAddressInForm = getPrimaryAddressFromFormStateAnswers(
      formState.values,
    );
  } catch (e) {
    console.error('Error getting primary address from form:', e);
    Bugsnag.notify(errorify(e));
  }
  const actualPrimaryAddress = primaryAddressInForm
    ? primaryAddressInForm
    : primaryAddress;

  if (
    !actualPrimaryAddress ||
    !questionInstanceApiName ||
    primaryAddressRegex.exec(questionInstanceApiName)
  ) {
    return null;
  }
  const formattedAddress = actualPrimaryAddress
    ? [
        actualPrimaryAddress.address,
        actualPrimaryAddress.city,
        actualPrimaryAddress.province,
        actualPrimaryAddress.postal,
      ]
        .filter((elem) => elem && elem !== 'N/A')
        .join(', ')
    : null;

  // check if the fields are already filled in with the values from the mailing address
  const addressFieldName = fillFields.address;
  const cityFieldName = fillFields.city;
  const provinceFieldName = fillFields.province;
  const postalFieldName = fillFields.postalCode;

  let isUsingPrimaryAddress = false; // if the current field already matches the mailing address
  if (
    actualPrimaryAddress &&
    // @ts-expect-error
    formApi.getValue(addressFieldName) === actualPrimaryAddress.address &&
    // @ts-expect-error
    formApi.getValue(cityFieldName) === actualPrimaryAddress.city &&
    // @ts-expect-error
    formApi.getValue(provinceFieldName) === actualPrimaryAddress.province &&
    // @ts-expect-error
    formApi.getValue(postalFieldName) === actualPrimaryAddress.postal
  ) {
    isUsingPrimaryAddress = true;
  } else {
    isUsingPrimaryAddress = false;
  }

  const buttonText = isUsingPrimaryAddress
    ? 'Already Using Primary Address'
    : 'Same As Primary Address';

  const handleClick = () => {
    if (!formApi) {
      console.error('No valid form api; exiting');
      return;
    }
    if (!actualPrimaryAddress) {
      console.error('No valid mailing address; exiting');
      return;
    }

    // now fill in the fields
    // TODO this is so stupid. Why is it postal in one and postalCode in the other? What was I thinking. Honestly.
    for (let fieldName of ['address', 'city', 'province']) {
      // @ts-expect-error
      formApi.setValue(fillFields[fieldName], actualPrimaryAddress[fieldName]);
      // @ts-expect-error
      formApi.setTouched(fillFields[fieldName], true);
    }
    // (and now the postal code)
    // @ts-expect-error
    formApi.setValue(fillFields['postalCode'], actualPrimaryAddress.postal);
    // @ts-expect-error
    formApi.setTouched(fillFields['postalCode'], true);
  };
  if (!onLifeOrg) {
    return null;
  }
  return (
    <Tooltip title={formattedAddress || 'No address defined'}>
      <Button
        className={classes.sameAsPrimaryAddressButton}
        variant='outlined'
        type='button'
        disabled={isUsingPrimaryAddress}
        onClick={handleClick}
      >
        {buttonText}
      </Button>
    </Tooltip>
  );
};

const recursivePrimaryAddressSearch = (
  answersList: Array<QuoteWizardAnswerInstance>,
): QuoteWizardAnswerInstance | undefined => {
  if (answersList.length === 0) {
    return;
  }
  for (let answer of answersList) {
    if (
      answer.questionInstance.component === 'location' &&
      primaryAddressRegex.exec(answer.questionInstance.apiName)
    ) {
      return answer;
    }
    const foundInSubAnswers = recursivePrimaryAddressSearch(answer.subAnswers);
    if (foundInSubAnswers) {
      return foundInSubAnswers;
    }
  }
  return undefined;
};

const mapStateToProps = (state: StoreState) => {
  const formsWithAnswers = Object.values(state.quoteWizard.formAnswers);
  let primaryAddress;
  let primaryAddressAnswer;
  for (let form of formsWithAnswers) {
    primaryAddressAnswer = recursivePrimaryAddressSearch(form.answers);
    if (primaryAddressAnswer) {
      break;
    }
  }
  if (primaryAddressAnswer) {
    const addressAnswer = primaryAddressAnswer.subAnswers.find(
      (subAnswer) => subAnswer.questionInstance.apiName === 'address',
    );
    const cityAnswer = primaryAddressAnswer.subAnswers.find(
      (subAnswer) => subAnswer.questionInstance.apiName === 'city',
    );
    const provinceAnswer = primaryAddressAnswer.subAnswers.find(
      (subAnswer) => subAnswer.questionInstance.apiName === 'province',
    );
    const postalAnswer = primaryAddressAnswer.subAnswers.find(
      (subAnswer) => subAnswer.questionInstance.apiName === 'postal',
    );
    primaryAddress = {
      address: addressAnswer ? addressAnswer.value : undefined,
      city: cityAnswer ? cityAnswer.value : undefined,
      province: provinceAnswer ? provinceAnswer.value : undefined,
      postal: postalAnswer ? postalAnswer.value : undefined,
    };
  }

  return {
    primaryAddress,
  };
};
const ConnectedSameAsPrimaryAddressButton = connect(
  mapStateToProps,
  null,
)((props) => <UnconnectedSameAsPrimaryAddressButton {...props} />);

export const SameAsPrimaryAddressButton = withStyles((_theme: Theme) => ({
  sameAsPrimaryAddressButton: {
    //marginBottom: theme.spacing(2),
  },
  // @ts-expect-error
}))((props: $TSFixMe) => <ConnectedSameAsPrimaryAddressButton {...props} />);
